import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { Option, Question } from "../../models/questionnaire.model";
import { NgForm, Validators } from "@angular/forms";
import { NgbDateStruct, NgbInputDatepicker } from "@ng-bootstrap/ng-bootstrap";
import { QuestionnaireService } from "../../services/questionnaire.service";
import { PlacesService } from "../../services/places.service";
import {
  Observable,
  debounceTime,
  distinctUntilChanged,
  finalize,
  of,
  switchMap,
  tap,
} from "rxjs";
import { ContentService } from "../../services/content.service";
import { calculateAge } from "../../utils/utils";
import { LocaleService } from "../../services/locale.service";

const translatedAnswerValueMap: Record<string, string> = {
  si: "Yes",
  no: "No",
  masculino: "Male",
  femenino: "Female",
  otro: "Other",
  otra: "Other",
  ingles: "English",
  english: "English",
  espanol: "Spanish",
  spanish: "Spanish",
  blanco: "White",
  blanca: "White",
  asiatico: "Asian",
  asiatica: "Asian",
  africano: "African",
  africana: "African",
  "hispano o latino": "Hispanic or Latino",
  "hispana o latina": "Hispanic or Latino",
  "hispano latino": "Hispanic or Latino",
  "hispana latina": "Hispanic or Latino",
  "no se": "Do Not Know",
  "no lo se": "Do Not Know",
  desconocido: "Do Not Know",
  desconocida: "Do Not Know",
  ninguno: "None",
  ninguna: "None",
  padre: "Parent",
  madre: "Parent",
  padres: "Parent",
  hijo: "Child",
  hija: "Child",
  hijos: "Child",
  hermano: "Sibling",
  hermana: "Sibling",
  hermanos: "Sibling",
  esposo: "Spouse",
  esposa: "Spouse",
  conyuge: "Spouse",
  pareja: "Partner",
  amigo: "Friend",
  amiga: "Friend",
  vegetariano: "Vegetarian",
  vegetariana: "Vegetarian",
  vegano: "Vegan",
  vegana: "Vegan",
  "sin gluten": "Gluten Free",
  vitaminas: "Vitamins",
  suplementos: "Supplements",
  nunca: "Never",
  actual: "Current",
  anteriormente: "Former",
  anterior: "Former",
};

const validationCopy = {
  en: {
    selectOption: "Please select an option",
    required: "This field is required",
    invalidInput: "Invalid input",
    completeDate: "Please enter a complete date",
    invalidDate: "Please enter a valid date",
    invalidPhone: "Please enter a valid phone number",
    valueBetween: (min: number | string, max: number | string) =>
      `Value must be between ${min} and ${max}`,
  },
  es: {
    selectOption: "Seleccione una opción",
    required: "Este campo es obligatorio",
    invalidInput: "Entrada no válida",
    completeDate: "Ingrese una fecha completa",
    invalidDate: "Ingrese una fecha válida",
    invalidPhone: "Ingrese un número de teléfono válido",
    valueBetween: (min: number | string, max: number | string) =>
      `El valor debe estar entre ${min} y ${max}`,
  },
};

@Component({
  selector: "app-questions",
  templateUrl: "./questions.component.html",
  styleUrl: "./questions.component.scss",
})
export class QuestionsComponent implements OnInit, AfterViewInit {
  @ViewChild("questionnaireForm") questionnaireForm!: NgForm;
  @ViewChildren(NgbInputDatepicker) datePickers!: QueryList<NgbInputDatepicker>;
  @Input() questionnaire: any;
  @Input() explanationText: string[] = [];
  @Input() isLoadingQuestionnaire = false;
  @Input() isSavingResponses = false;
  @Input() hasCompleted = false;
  @Input() errorMessage: string | null = null;
  @Input() currentSectionIndex = 0;
  @Input() questionnaireType!: "initial" | "comprehensive";
  @Input() submitButtonText = "Submit";
  @Input() nextSectionButtonText = "Next Section";
  @Input() adjustingQuestionsText = "Adjusting questions based on your responses...";
  @Input() selectOptionErrorText = "Please select an option";
  @Input() invalidInputErrorText = "Invalid input";

  @Output() formInstance = new EventEmitter<NgForm>();
  @Output() formSubmit = new EventEmitter<{
    currentSectionIndex: number;
    submittedAnswers: Record<string, any>;
  }>();
  @Output() currentStepChange = new EventEmitter<number>();

  // Track user answers
  answers: Record<string, any> = {};
  selectedOptions: Record<string, Record<string, boolean>> = {};
  isSingleSection = false;
  isCurrentSectionValid = false;
  submittingSectionIndex = 0;
  profileInfo: any;

  suggestions: any[] = [];

  isLoading = false;
  isSearchingAddress = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private questionnaireService: QuestionnaireService,
    private placesService: PlacesService,
    private contentService: ContentService,
    private localeService: LocaleService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    const currentSection =
      this.questionnaire.sections[this.currentSectionIndex];

    if (this.questionnaireType == "comprehensive") {
      this.contentService.getDonorProfile().subscribe({
        next: (profileInfo) => {
          this.profileInfo = profileInfo;

          this.initializeAndSetVisibility(currentSection);
          this.isLoading = false;
        },
        error: () => {
          console.log(
            "failed to get profile info, will still initialize questions"
          );

          this.initializeAndSetVisibility(currentSection);
          this.isLoading = false;
        },
      });
    } else {
      this.initializeAndSetVisibility(currentSection);
      this.isLoading = false;
    }

    Object.keys(this.answers).forEach((questionId) => {
      if (Array.isArray(this.answers[questionId])) {
        this.selectedOptions[questionId] = {};
        this.answers[questionId].forEach((option: string) => {
          this.selectedOptions[questionId][option] = true;
        });
      }
    });
  }

  getChoiceErrorMessage(question: Question): string {
    return this.translateValidationMessage(
      question.validation_rules?.errorMessage,
      this.validationText.selectOption
    );
  }

  getInputErrorMessage(question: Question): string {
    const control = this.questionnaireForm?.form.get(question.question_id);
    const errors = control?.errors || {};

    if (errors["required"]) {
      return this.validationText.required;
    }

    if (errors["invalidDate"]) {
      const invalidDateError = errors["invalidDate"];
      if (invalidDateError === "completeDate") {
        return this.validationText.completeDate;
      }

      return this.translateValidationMessage(
        typeof invalidDateError === "string" ? invalidDateError : undefined,
        this.validationText.invalidDate
      );
    }

    if (errors["range"]) {
      return this.translateValidationMessage(
        typeof errors["range"] === "string" ? errors["range"] : undefined,
        this.getRangeErrorMessage(question)
      );
    }

    if (errors["pattern"]) {
      if (question.question_type === "phone_number") {
        return this.validationText.invalidPhone;
      }

      return this.translateValidationMessage(
        question.validation_rules?.errorMessage,
        this.validationText.invalidInput
      );
    }

    return this.translateValidationMessage(
      question.validation_rules?.errorMessage,
      this.validationText.invalidInput
    );
  }

  private get validationText() {
    return (
      validationCopy[
        this.localeService.currentLocale as keyof typeof validationCopy
      ] || validationCopy.en
    );
  }

  private getRangeErrorMessage(question: Question): string {
    const min = question.validation_rules?.min;
    const max = question.validation_rules?.max;

    if (min !== undefined && max !== undefined) {
      return this.validationText.valueBetween(min, max);
    }

    return this.validationText.invalidInput;
  }

  private translateValidationMessage(
    message: string | undefined,
    fallback: string
  ): string {
    if (!message) {
      return fallback;
    }

    if (this.localeService.currentLocale !== "es") {
      return message;
    }

    const normalizedMessage = message
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

    if (normalizedMessage.includes("complete date")) {
      return validationCopy.es.completeDate;
    }

    if (normalizedMessage.includes("valid date")) {
      return validationCopy.es.invalidDate;
    }

    if (normalizedMessage.includes("phone")) {
      return validationCopy.es.invalidPhone;
    }

    const rangeMatch = normalizedMessage.match(
      /value must be between\s+([^\s]+)\s+and\s+([^\s]+)/
    );
    if (rangeMatch) {
      return validationCopy.es.valueBetween(rangeMatch[1], rangeMatch[2]);
    }

    if (
      normalizedMessage.includes("select an option") ||
      normalizedMessage.includes("please select")
    ) {
      return validationCopy.es.selectOption;
    }

    if (
      normalizedMessage.includes("required") ||
      normalizedMessage.includes("obligatorio")
    ) {
      return validationCopy.es.required;
    }

    if (normalizedMessage.includes("invalid")) {
      return validationCopy.es.invalidInput;
    }

    return fallback;
  }

  ngAfterViewInit() {
    this.subscribeToFormChangesAndAddHandler();
  }

  subscribeToFormChangesAndAddHandler() {
    if (this.questionnaireForm) {
      this.questionnaireForm.valueChanges
        ?.pipe(debounceTime(200), distinctUntilChanged())
        .subscribe((values) => {
          this.checkVisibilityAndValidityforAllQuestions();
        });

      this.formInstance.emit(this.questionnaireForm);
      this.addFocusHandler();
    }
  }

  initializeAndSetVisibility(currentSection: any) {
    currentSection.subsections.forEach((subsection: any) => {
      subsection.questions.forEach((question: Question) => {
        if (!this.selectedOptions[question.question_id]) {
          this.selectedOptions[question.question_id] = {};
        }

        if (Array.isArray(this.answers[question.question_id])) {
          this.answers[question.question_id].forEach((option: string) => {
            this.selectedOptions[question.question_id][option] = true;
          });
        }
        question.is_visible = this.isQuestionVisible(question);
        // Mark as dependent if it has visibility logic
        question.is_dependent = !!question.visibility_logic;
      });

      subsection.is_visible = true;

      // If subsection has visibilityLogic, call API to determine visibility
      if (subsection.visibilityLogic) {
        this.questionnaireService
          .checkSubsectionVisibility(subsection.subsection_id)
          .subscribe({
            next: (response) => {
              subsection.is_visible = response.visible;
            },
          });
      }
    });
  }

  search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => (this.isSearchingAddress = true)),
      switchMap((term) =>
        term.length > 3
          ? this.placesService.getAddressSuggestions(term).pipe(
              finalize(() => (this.isSearchingAddress = false)) // hide spinner
            )
          : of([]).pipe(finalize(() => (this.isSearchingAddress = false)))
      )
    );

  formatResult = (result: any) => result.Text;

  addFocusHandler() {
    const container = document.getElementById("questionnaireForm");

    container?.addEventListener("keydown", (event) => {
      const formGroups = Array.from(
        container.querySelectorAll(".form-group")
      ) as HTMLElement[];

      // Filter visible form groups (skipping those with display: none)
      const focusableElements: HTMLElement[] = formGroups
        .filter((group) => window.getComputedStyle(group).display !== "none")
        .flatMap((group) =>
          Array.from(
            group.querySelectorAll(
              'input, button, textarea, [tabindex]:not([tabindex="-1"])'
            )
          ) as HTMLElement[]
        );

      // Find standalone elements that are not inside form groups and are visible
      const standaloneElements = Array.from(
        container.querySelectorAll(
          ':scope > input, :scope > button, :scope > textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(
        (element) => window.getComputedStyle(element).display !== "none"
      ) as HTMLElement[];

      // Combine the focusable elements and standalone elements
      focusableElements.push(...standaloneElements);

      // Remove duplicates and sort by appearance in the DOM
      const uniqueFocusableElements = Array.from(new Set(focusableElements));

      const currentIndex = uniqueFocusableElements.indexOf(
        document.activeElement as HTMLElement
      );

      // Handle Tab and Shift + Tab for navigation
      if (event.key === "Tab") {
        event.preventDefault();

        const nextIndex = event.shiftKey
          ? (currentIndex - 1 + uniqueFocusableElements.length) %
            uniqueFocusableElements.length // Navigate backward
          : (currentIndex + 1) % uniqueFocusableElements.length; // Navigate forward

        uniqueFocusableElements[nextIndex]?.focus();
        return;
      }

      // Handle ArrowUp, ArrowDown, ArrowLeft, and ArrowRight keys for navigation within the current form-group
      if (
        ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)
      ) {
        const currentGroup = formGroups
          .filter((group) => window.getComputedStyle(group).display !== "none")
          .find((group) => group?.contains(document.activeElement));

        if (currentGroup) {
          const groupInputs = Array.from(
            currentGroup?.querySelectorAll(
              'input, button, textarea, [tabindex]:not([tabindex="-1"])'
            ) || []
          ) as HTMLElement[];

          const currentGroupIndex = groupInputs.indexOf(
            document.activeElement as HTMLElement
          );

          if (currentGroupIndex !== -1) {
            let nextGroupIndex: number | null = null;

            if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
              event.preventDefault();
              nextGroupIndex =
                (currentGroupIndex - 1 + groupInputs.length) %
                groupInputs.length;
            } else if (
              event.key === "ArrowDown" ||
              event.key === "ArrowRight"
            ) {
              event.preventDefault();
              nextGroupIndex = (currentGroupIndex + 1) % groupInputs.length;
            }

            if (nextGroupIndex !== null) {
              groupInputs[nextGroupIndex]?.focus();
            }
          }
        }
      }

      // Handle Enter key for toggling/selecting the currently focused field
      if (event.key === "Enter") {
        const activeElement = document.activeElement as HTMLElement;

        if (activeElement.tagName === "INPUT") {
          const input = activeElement as HTMLInputElement;

          if (input.type === "checkbox" || input.type === "radio") {
            event.preventDefault();
            input.checked = !input.checked; // Toggle checkbox or radio
            input.dispatchEvent(new Event("change")); // Trigger change event
          }
        } else if (activeElement.tagName === "BUTTON") {
          event.preventDefault();
          activeElement.click(); // Trigger button click
        }
      }
    });
  }
  // Group questions by rowGroup for each subsection
  groupQuestionsByRow(questions: Question[]): [string, Question[]][] {
    const grouped: [string, Question[]][] = [];

    const filteredQuestions = questions.filter(
      (q) => !q.is_overarching_question
    );

    filteredQuestions.forEach((question) => {
      const rowKey =
        question.rowGroup == null || question.rowGroup < 0
          ? `unique_${question.question_id}`
          : `group_${question.rowGroup}`;

      const existingGroup = grouped.find(([key]) => key === rowKey);
      if (existingGroup) {
        existingGroup[1].push(question);
      } else {
        grouped.push([rowKey, [question]]);
      }
    });

    // Sort the groups based on the `order` field
    grouped.sort(([keyA, questionsA], [keyB, questionsB]) => {
      const aMinOrder = Math.min(...questionsA.map((q) => q.order ?? Infinity));
      const bMinOrder = Math.min(...questionsB.map((q) => q.order ?? Infinity));
      return aMinOrder - bMinOrder;
    });
    return grouped;
  }

  getOverachingQuestionFromSubsection(questions: Question[]) {
    return (
      questions.filter((question) => question.is_overarching_question) || null
    );
  }

  getOptionValue(option: Option): string {
    const stableValue =
      option.value ||
      option.optionValue ||
      option.option_value ||
      option.answerValue ||
      option.key;

    return this.normalizeAnswerValue(stableValue || option.text);
  }

  private normalizeAnswerValue(value: string): string {
    const normalized = value.trim();
    const lookupKey = this.normalizeLookupKey(normalized);

    if (lookupKey.includes("no tengo antecedentes familiares")) {
      return "I have no family history";
    }

    return translatedAnswerValueMap[lookupKey] || normalized;
  }

  private normalizeLookupKey(value: string): string {
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  }

  onOverarchingQuestionChange(
    overarchingQuestionId: string,
    selectedOption: Option,
    subsection: any
  ) {
    if (this.getOptionValue(selectedOption) === "No") {
      subsection.questions.forEach((question: any) => {
        if (
          question.question_type === "single" &&
          question.question_id !== overarchingQuestionId
        ) {
          this.answers[question.question_id] = "No";
        }
      });
    }
  }

  // Evaluate visibility logic
  isQuestionVisible(question: Question): boolean {
    if (!question.visibility_logic) {
      return true; // Always visible if no visibility logic
    }

    try {
      let isVisible = false;
      if (
        this.questionnaireType == "comprehensive" &&
        question.visibility_logic.dependsOn === "age"
      ) {
        if (!this.profileInfo) {
          console.warn(
            "Profile info not loaded yet. Showing question by default."
          );
          return true;
        }
        // Get age from profile data
        this.answers["age"] = calculateAge(this.profileInfo?.data?.donor?.dateOfBirth) || null;
      }

      // Get the answer for the dependent question

      let dependsOnAnswer;

      if (question.visibility_logic.dependsOn === "profileField") {
        const fieldName = question.visibility_logic?.fieldName;
        if (fieldName) {
          dependsOnAnswer = this.profileInfo?.data?.donor?.[fieldName];
        }
      } else {
        dependsOnAnswer = this.answers[question.visibility_logic.dependsOn];
      }

      let values: any;

      if (typeof question.visibility_logic.value === "number") {
        values = question.visibility_logic.value;
      } else if (typeof question.visibility_logic.value === "string") {
        values = question.visibility_logic.value
          .split("|")
          .map((value: string) => value.toLowerCase().trim());
      }

      // Evaluate the visibility based on the operator
      switch (question.visibility_logic.operator) {
        case "equals":
          if (Array.isArray(dependsOnAnswer)) {
            isVisible = dependsOnAnswer.some((value: string) =>
              values.includes(value.toLowerCase())
            );
          } else {
            isVisible = values.includes(dependsOnAnswer?.toLowerCase());
          }

          break;

        case "not_equals":
          if (Array.isArray(dependsOnAnswer)) {
            isVisible = !dependsOnAnswer.some((value: string) =>
              values.includes(value.toLowerCase())
            );
          } else {
            isVisible = !values.includes(dependsOnAnswer?.toLowerCase());
          }
          break;

        case "greater_than":
          isVisible = +dependsOnAnswer > +values;
          break;

        case "less_than":
          isVisible = +dependsOnAnswer < +values;
          break;

        case "greater_than_or_equal":
          isVisible = +dependsOnAnswer >= +values;
          break;

        case "less_than_or_equal":
          isVisible = +dependsOnAnswer <= +values;
          break;

        default:
          isVisible = true; // Default to visible if the operator is not recognized
      }

      if (this.questionnaireForm) {
        const control = this.questionnaireForm.form.get(question.question_id);

        if (control) {
          if (isVisible) {
            if (!control.hasValidator(Validators.required)) {
              control.addValidators(Validators.required);
            }
          } else {
            if (control.hasValidator(Validators.required)) {
              control.removeValidators(Validators.required);
            }
          }
        }
      }

      return isVisible;
    } catch (error) {
      console.error(
        "Error parsing visibility logic:",
        question.visibility_logic,
        error
      );
      return true; // Show by default if visibility logic is invalid
    }
  }

  // Update option visibility in place
  areOptionsVisible(question: Question): void {
    if (!question.options) return;

    question.options.forEach((option: any) => {
      const logic = option.visibilityLogic;

      if (!logic) {
        option.isHidden = false;
        return;
      }

      const dependsOnAnswer = this.answers[logic.dependsOn];
      const logicValue = this.normalizeAnswerValue(String(logic.value));
      let isVisible = true;

      if (dependsOnAnswer !== undefined && dependsOnAnswer !== null) {
        if (logic.operator === "equals") {
          isVisible = Array.isArray(dependsOnAnswer)
            ? dependsOnAnswer.includes(logicValue)
            : dependsOnAnswer === logicValue;
        } else if (logic.operator === "not_equals") {
          isVisible = Array.isArray(dependsOnAnswer)
            ? !dependsOnAnswer.includes(logicValue)
            : dependsOnAnswer !== logicValue;
        }
      }

      option.isHidden = !isVisible;

      if (option.isHidden) {
        const optionValue = this.getOptionValue(option);
        // Deselect hidden option
        if (this.selectedOptions[question.question_id]?.[optionValue]) {
          delete this.selectedOptions[question.question_id][optionValue];
        }

        if (Array.isArray(this.answers[question.question_id])) {
          this.answers[question.question_id] = this.answers[question.question_id].filter(
            (val: string) => val !== optionValue
          );
        } else if (this.answers[question.question_id] === optionValue) {
          delete this.answers[question.question_id];
        }
      }
    });
  }

  doThisTwice(checked: boolean, questionId: string, option: string) {
    let i = 0;

    while (i < 1) {
      i++;
      console.log("hi");
      this.onMultiCheckboxChange(checked, questionId, option);
      // setTimeout(() => {
      //   this.onMultiCheckboxChange(checked, questionId, option);
      // }, 500);
    }
  }

  onMultiCheckboxChange(checked: boolean, questionId: string, option: string) {
    if (!this.selectedOptions[questionId]) {
      this.selectedOptions[questionId] = {};
    }

    if (!Array.isArray(this.answers[questionId])) {
      this.answers[questionId] = [];
    }

    const currentSection =
      this.questionnaire.sections[this.currentSectionIndex];
    const subsection = currentSection.subsections.find((sub: any) =>
      sub.questions.some((q: any) => q.question_id === questionId)
    );
    const isFamilyHistory = subsection?.name === "familyHistory";

    this.selectedOptions[questionId][option] = checked;

    if (checked) {
      if (isFamilyHistory) {
        if (option.includes("I have no family history")) {
          Object.keys(this.selectedOptions[questionId]).forEach((key) => {
            if (!key.includes("I have no family history")) {
              this.selectedOptions[questionId][key] =
                key === option ? checked : false;
            }
          });

          this.answers[questionId] = this.answers[questionId].filter(
            (val: string) => val.includes("I have no family history")
          );
        } else {
          Object.keys(this.selectedOptions[questionId]).forEach((key) => {
            if (key.includes("I have no family history")) {
              this.selectedOptions[questionId][key] =
                key === option ? checked : false;
            }
          });
          this.answers[questionId] = this.answers[questionId].filter(
            (val: string) => !val.includes("I have no family history")
          );
        }
      }

      if (!this.answers[questionId].includes(option)) {
        this.answers[questionId].push(option);
      }
    } else {
      this.answers[questionId] = this.answers[questionId].filter(
        (selectedOption: string) => selectedOption !== option
      );
    }


    if (this.answers[questionId].length === 0) {
      delete this.answers[questionId];
    }
  }

  onShortAnswerChange(value: string, question: Question): void {
    this.answers[question.question_id] = this.isCurrencyQuestion(question)
      ? this.formatUsdInput(value)
      : value;
  }

  isCurrencyQuestion(question: Question): boolean {
    const text = this.normalizeQuestionText(question.questiion_text);
    return (
      /current.*income/.test(text) ||
      /household.*income/.test(text) ||
      /disposable.*income/.test(text) ||
      /ingreso.*actual/.test(text) ||
      /ingresos.*actuales/.test(text) ||
      /ingreso.*hogar/.test(text) ||
      /ingresos.*hogar/.test(text) ||
      /ingreso.*disponible/.test(text) ||
      /ingresos.*disponibles/.test(text)
    );
  }

  private formatUsdInput(value: string): string {
    const digits = String(value || "").replace(/\D/g, "");

    if (!digits) {
      return "";
    }

    return `$${Number(digits).toLocaleString("en-US")}`;
  }

  private normalizeQuestionText(value: string): string {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }

  validateMultiSelectField(questionId: string) {
    const currentSection =
      this.questionnaire.sections[this.currentSectionIndex];

    const question = currentSection.subsections
      .flatMap((subsection: any) => subsection.questions)
      .find((q: any) => q.question_id === questionId);

    question.is_valid = this.answers[questionId]?.length > 0;
    if (!question.is_valid) {
      this.questionnaireForm.form
        .get(questionId)
        ?.setErrors({ required: true });
    }
  }

  toggleDatePicker(questionId: string): void {
    const datePicker = this.datePickers.find((dp) => {
      const elementId = dp["_elRef"].nativeElement.id;
      return elementId === `dp_${questionId}`;
    });

    if (datePicker) {
      datePicker.toggle();
    }
  }

  // Handle date change from the datepicker
  onDateChange(date: NgbDateStruct, question: Question): void {
    if (date) {
      this.answers[question.question_id] = date; // Store the date as structured data
    }
  }

  // Date range
  minDate: NgbDateStruct = { year: 1900, month: 1, day: 1 };
  maxDate: NgbDateStruct = this.getTomorrowDate();

  // Function to calculate tomorrow's date
  getTomorrowDate(): NgbDateStruct {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return {
      year: tomorrow.getFullYear(),
      month: tomorrow.getMonth() + 1, // JavaScript months are 0-based
      day: tomorrow.getDate(),
    };
  }

  checkDateValidity(question: Question) {
    if (
      !question ||
      question.question_type != "date" ||
      !question.validation_rules
    )
      return;

    const date = this.answers[question.question_id] as NgbDateStruct;

    if (!date || !date.year || !date.month || !date.day) {
      this.questionnaireForm.controls[question.question_id].setErrors({
        invalidDate: "completeDate",
      });
      return;
    }

    const enteredDate = new Date(date.year, date.month - 1, date.day);
    const today = new Date();

    const { operator, value, conditionType, errorMessage } =
      question.validation_rules;

    const numericValue = Number(value);

    let age = today.getFullYear() - enteredDate.getFullYear();
    const monthDiff = today.getMonth() - enteredDate.getMonth();
    const dayDiff = today.getDate() - enteredDate.getDate();

    // Adjust age if the birthday hasn't happened yet this year
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }

    if (
      conditionType === "date" &&
      operator === "less_than" &&
      age < numericValue
    ) {
      this.questionnaireForm.controls[question.question_id].setErrors({
        invalidDate: errorMessage,
      });
    } else {
      this.questionnaireForm.controls[question.question_id].setErrors(null); // Clear errors if valid
    }
  }

  formatPhoneNumber(event: any, questionId: string) {
    let input = event.replace(/\D/g, ""); // Remove all non-numeric characters

    if (input.length > 10) {
      input = input.substring(0, 10); // Limit to 10 digits
    }

    // Format as (XXX) XXX-XXXX
    let formatted = "";
    if (input.length > 6) {
      formatted = `(${input.substring(0, 3)}) ${input.substring(3, 6)}-${input.substring(6)}`;
    } else if (input.length > 3) {
      formatted = `(${input.substring(0, 3)}) ${input.substring(3)}`;
    } else {
      formatted = input;
    }

    // Set formatted value in model
    this.answers[questionId] = formatted;
  }

  checkVisibilityAndValidityforAllQuestions() {
    const currentSection =
      this.questionnaire.sections[this.currentSectionIndex];

    this.isCurrentSectionValid = true; // Assume valid initially

    currentSection?.subsections.forEach((subsection: any) => {
      let shouldUpdateOverarchingQuestion = false;
      const overarchingQuestions: Question[] =
        this.getOverachingQuestionFromSubsection(
          subsection.questions
        ) as Question[];

      let hasVisibleQuestions = false;

      subsection.questions.forEach((question: Question) => {
        const control = this.questionnaireForm.form.get(question.question_id);
        const isVisible = this.isQuestionVisible(question);

        if (control) {
          if (isVisible) {
            question.is_visible = true;
            if (
              ["single", "multi"].includes(question.question_type) &&
              Array.isArray(question.options)
            ) {
              this.areOptionsVisible(question);

              const hasAnyVisibleOption = question.options.some(
                (opt) => !opt.isHidden
              );
              if (hasAnyVisibleOption) {
                hasVisibleQuestions = true;
              }

              this.validateMultiSelectField(question.question_id);
            } else {
              hasVisibleQuestions = true;
            }

            if (question.visibility_logic) {
              question.is_required = true;
            }

            if (question.question_type === "multi") {
              this.areOptionsVisible(question);
              this.validateMultiSelectField(question.question_id);
              // console.log("options: ",question.options);
            }

            if (
              question.question_type === "date" &&
              this.answers[question.question_id]
            ) {
              this.checkDateValidity(question);
            }
            if (
              question.question_type === "short" &&
              question.validation_rules?.min !== undefined &&
              question.validation_rules?.max !== undefined
            ) {
              const value = Number(this.answers[question.question_id]);
              const { min, max, errorMessage } = question.validation_rules;

              if (isNaN(value) || value < min || value > max) {
                control.setErrors({
                  range:
                    errorMessage || `Value must be between ${min} and ${max}`,
                });
                this.isCurrentSectionValid = false;
              } else if (!control.errors?.["required"]) {
                control.setErrors(null); // Only clear if not still failing required
              }
            }
            // control.enable();
          } else {
            // Clear the value ONLY IF it was previously visible
            if (question.is_visible) {
              this.answers[question.question_id] = null;
              control.setValue(null, { emitEvent: false }); // Prevents triggering another event
            }

            question.is_visible = false;
            question.is_required = false;
            // control.disable();
            control.setErrors(null);
          }

          if (
            overarchingQuestions &&
            overarchingQuestions.length > 0 &&
            question.question_id !== overarchingQuestions[0].question_id && // Exclude overarching question itself
            question.question_type === "single" &&
            question.options &&
            question.options.some((opt) => this.getOptionValue(opt) === "Yes") &&
            this.answers[question.question_id] === "Yes"
          ) {
            shouldUpdateOverarchingQuestion = true;
          }

          // If any control is invalid, mark section as invalid
          if (control.invalid) {
            this.isCurrentSectionValid = false;
          }
        }
      });

      subsection.is_visible =
        hasVisibleQuestions || subsection.name == "helpText";

      if (
        shouldUpdateOverarchingQuestion &&
        overarchingQuestions &&
        overarchingQuestions.length > 0
      ) {
        const overarchingQuestionId = overarchingQuestions[0].question_id;
        this.answers[overarchingQuestionId] = "Yes";
        this.questionnaireForm.form
          .get(overarchingQuestionId)
          ?.setValue("Yes", { emitEvent: false });
      }
    });

    this.cdr.detectChanges();
  }

  markAllFieldsAsTouched(): void {
    const currentSection =
      this.questionnaire.sections[this.currentSectionIndex];

    currentSection?.subsections.forEach((subsection: any) => {
      subsection.questions.forEach((question: Question) => {
        const control = this.questionnaireForm.form.get(question.question_id);
        if (control) {
          control.markAsTouched();
        }
      });
    });

    this.checkVisibilityAndValidityforAllQuestions();
  }

  scrollToFirstInvalidField(): void {
    const invalidField = document.querySelector(
      ".ng-invalid:not(form)" // Find the first invalid field (excluding the form itself)
    ) as HTMLElement;

    if (invalidField) {
      invalidField.scrollIntoView({ behavior: "smooth", block: "center" });
      invalidField.focus();
    }
  }

  // Method to navigate to the next section
  nextSection() {
    this.markAllFieldsAsTouched();

    if (this.isCurrentSectionValid) {
      this.submittingSectionIndex = this.currentSectionIndex;
      this.currentSectionIndex++;
      this.currentStepChange.emit(this.currentSectionIndex);

      this.onSubmit();
    } else {
      this.scrollToFirstInvalidField();
    }
  }

  onSubmit() {
    if (this.currentSectionIndex == this.questionnaire.sections.length - 1) {
      this.markAllFieldsAsTouched();
    }

    if (this.isCurrentSectionValid) {
      this.formSubmit.emit({
        currentSectionIndex: this.submittingSectionIndex,
        submittedAnswers: this.answers,
      });
    } else {
      this.scrollToFirstInvalidField();
    }
  }
}
